/**
 * TemplateLibrary - Bibliothèque de templates de prompts
 * 
 * Affiche les templates disponibles pour un type d'agent donné
 * et permet de les utiliser pour remplir le champ Instructions
 */
import React, { useMemo } from 'react'
import { BookTemplate } from 'lucide-react'
import { AgentType, PromptTemplate, getTemplatesByType } from '../data/promptTemplates'
import TemplateCard from './TemplateCard'
import { useAppLanguage } from '../i18n'

interface TemplateLibraryProps {
  agentType: AgentType
  currentPrompt: string
  onSelectTemplate: (template: PromptTemplate) => void
  className?: string
}

export default function TemplateLibrary({ 
  agentType, 
  currentPrompt, 
  onSelectTemplate,
  className = '' 
}: TemplateLibraryProps) {
  const { language, t } = useAppLanguage()
  const ae = t.agents.agentEditor
  const templates = useMemo(() => getTemplatesByType(agentType, language), [agentType, language])
  
  // Détecter si un template est actuellement utilisé
  const usedTemplateId = useMemo(() => {
    const template = templates.find(t => t.content === currentPrompt)
    return template?.id || null
  }, [templates, currentPrompt])

  if (templates.length === 0) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <BookTemplate className="w-12 h-12 mx-auto mb-3 text-zinc-600" />
        <p className="text-sm text-zinc-500">
          {ae.templateNoAvailable}
        </p>
      </div>
    )
  }

  // Séparer les templates recommandés des autres
  const recommendedTemplates = templates.filter(t => t.recommended)
  const otherTemplates = templates.filter(t => !t.recommended)

  return (
    <div className={className}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BookTemplate className="w-5 h-5 text-emerald-400" />
          <h3 className="text-sm font-medium text-zinc-300">
            {ae.templateRecommended}
          </h3>
        </div>
        <span className="text-xs text-zinc-500">
          {ae.templateCount
            .replace('{n}', String(templates.length))
            .replace(/\{s\}/g, templates.length > 1 ? 's' : '')}
        </span>
      </div>

      {/* Recommended Template - Full Width */}
      {recommendedTemplates.length > 0 && (
        <div className="mb-6">
          {recommendedTemplates.map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              onUse={onSelectTemplate}
              isUsed={template.id === usedTemplateId}
            />
          ))}
        </div>
      )}

      {/* Other Templates - 50/50 Grid */}
      {otherTemplates.length > 0 && (
        <>
          <div className="flex items-center gap-2 mb-4 pt-4 border-t border-zinc-800">
            <h4 className="text-sm font-medium text-zinc-400">
              {ae.templateOthers}
            </h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {otherTemplates.map(template => (
              <TemplateCard
                key={template.id}
                template={template}
                onUse={onSelectTemplate}
                isUsed={template.id === usedTemplateId}
              />
            ))}
          </div>
        </>
      )}

      {/* Info box */}
      <div className="mt-6 p-4 bg-blue-900/20 border border-blue-800/30 rounded-lg">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            💡
          </div>
          <div className="flex-1 text-sm text-blue-400">
            <p className="font-medium mb-1">{ae.templateTipTitle}</p>
            <p className="text-blue-400/80">
              {ae.templateTipBody}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
